/**
 * Central declaration of which tables expose which sortable columns.
 *
 * Required invariant (FR-025): a column may appear here only when the matching
 * endpoint actually **honours** that key. A clickable header over a column the
 * backend silently ignores draws an arrow on data that never moves — worse than
 * having no control at all.
 *
 * The reference whitelist lives in
 * `specs/008-admin-table-column-sort/contracts/list-sorting-api.md` § 3 and is
 * checked automatically by `tableSortConfig.test.ts`.
 */
import type { ColumnSortSpec } from '../hooks/useTableSort';

export type TableSortKey =
  | 'withdrawalRequests'
  | 'flaggedWithdrawals'
  | 'manualGameDeposits'
  | 'playerWalletTransactions'
  | 'players'
  | 'creditTransactions'
  | 'cashTransactions'
  | 'usdWallets'
  | 'providerWalletTransactions'
  | 'cashiers'
  | 'fraudFlags'
  | 'paymentTransactions'
  | 'agentBalances'
  | 'gameWallets';

export const TABLE_SORT_CONFIG: Record<TableSortKey, ColumnSortSpec[]> = {
  // GET /api/payment/admin/withdrawals/pending
  withdrawalRequests: [
    { key: 'amount', label: 'Amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],

  // Same endpoint as withdrawalRequests, different filters.
  flaggedWithdrawals: [
    { key: 'amount', label: 'Amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'createdAt', label: 'Date', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],

  // GET /api/admin/manual-game-deposits
  manualGameDeposits: [
    { key: 'amount', label: 'Amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],

  // GET /api/admin/users/:userId/wallet/transactions
  playerWalletTransactions: [
    { key: 'created_at', label: 'Date', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'type', label: 'Type', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
  ],

  // GET /api/admin/users
  // The backend also allows email/phone/banned, but the UI does not expose them.
  players: [
    { key: 'created_at', label: 'Created at', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'last_login_at', label: 'Last activity at', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
  ],

  // GET /api/admin/credit-wallets/ledger
  creditTransactions: [
    { key: 'created', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
    { key: 'from', label: 'From', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'to', label: 'To', valueKind: 'text', defaultDirection: 'ASC' },
  ],

  // GET /api/admin/cash-transactions
  cashTransactions: [
    { key: 'creditAmount', label: 'Credits', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'cashReceived', label: 'Cash', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'buyerCostRate', label: 'Cost', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  usdWallets: [
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'balanceBefore', label: 'Balance before', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'balanceAfter', label: 'Balance after', valueKind: 'money', defaultDirection: 'DESC' },
  ],
  providerWalletTransactions: [
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  cashiers: [
    { key: 'username', label: 'Username', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'lastLoginAt', label: 'Last login', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  fraudFlags: [
    { key: 'severity', label: 'Severity', valueKind: 'ranked', defaultDirection: 'DESC' },
    { key: 'createdAt', label: 'Created At', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  // GET /api/admin/transactions
  paymentTransactions: [
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'amount', label: 'USD amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'creditAmount', label: 'Credit amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'created_at', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  // GET /api/admin/agent-balances
  agentBalances: [
    { key: 'providerName', label: 'Game', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'providerCode', label: 'Code', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
    { key: 'currency', label: 'Currency', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'balance', label: 'Balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'lastSyncedAt', label: 'Last synced', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
  // GET /api/admin/game-wallets
  gameWallets: [
    { key: 'walletBalance', label: 'Temp Wallet Balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'inGameBalance', label: 'In-Game Balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'lastSyncedAt', label: 'Last Sync', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
};

export function getTableSortColumns(table: TableSortKey): ColumnSortSpec[] {
  return TABLE_SORT_CONFIG[table];
}
